/**
 * PostHog analytics wrapper for the Yesplan app.
 * See docs/POSTHOG.md for how events are sent, error handling, and how to add a new event.
 */
import posthog from 'posthog-js';

/* Expose the posthog singleton on window so Cypress tests can spy on it.
 * posthog-js as an ES module does not set window.posthog automatically. */
if (typeof window !== 'undefined' && typeof Cypress !== 'undefined') {
  window.posthog = posthog;
}

// Event name constants. Always use these instead of raw strings to prevent typos.
export const PH_USER_LOGGED_IN = 'user logged in'; // { tenant }
export const PH_USER_LOGGED_OUT = 'user logged out';
export const PH_SETTINGS_CLICKED = 'settings clicked';
export const PH_NOTIFICATION_CENTER_CLICKED = 'notification center clicked';
export const PH_NOTIFICATION_SETTINGS_CHANGED = 'notification settings changed'; // { email: boolean }
export const PH_BACK_BUTTON_CLICKED = 'back button clicked'; // { textBackButton }
export const PH_SETTING_DEFAULT_EVENT_TAB_CHANGED = 'setting default event tab changed'; // { tabId }
export const PH_TAB_CHANGED_ON_EVENT_DETAIL = 'tab changed on event detail'; // { previousTab, newTab }
export const PH_WEEK_NAVIGATED = 'week navigated'; // { direction, widgetName?, goToWeek: { startDate, endDate }, date? }
export const PH_EVENT_DAY_OPENED = 'event day opened'; // { date }
export const PH_EVENT_DETAIL_CLICKED = 'event detail clicked'; // { eventId, eventTitle, openedOnTab, clickedInContextOf }
export const PH_EVENTS_FILTER_APPLIED = 'events filter applied'; // { filterCount }
export const PH_EVENTS_FILTER_OPENED = 'events filter opened'; // { filterIndex, editExisting }
export const PH_ROSTER_DAY_TOGGLED = 'roster day toggled'; // { label, date, isVisible }
export const PH_TIMESHEET_DAY_CLICKED = 'timesheet day clicked'; // { date }
export const PH_TIMESHEET_EDITED = 'timesheet edited';
export const PH_TIMESHEET_SUBMITTED = 'timesheet submitted'; // { date, requiresApproval }

export const phWidgetClicked = (widgetId) => `${widgetId} widget clicked`;

/**
 * Returns whether PostHog tracking should be initialised for the given tenant.
 * 'testplan' is always excluded. localhost/staging are only included when
 * running inside Cypress so dev sessions don't pollute analytics. They can be toggled for testing purposes.
 */
export function usePosthogForTenant(tenant) {
  if (!tenant) return false;
  const logLocalhost = true;
  const logStaging = true;
  if (tenant === 'testplan') return false;
  /* posthog needs to be initialized in cypress tests and gets stubbed there */
  if ((tenant === 'localhost' || tenant === 'staging') && typeof Cypress !== 'undefined')
    return true;
  if (!logLocalhost && tenant === 'localhost') return false;
  if (!logStaging && tenant === 'staging') return false;
  return true;
}

/** Extracts the tenant name from a hostname string (e.g. 'acme.yesplan.be' → 'acme'). */
export function getTenantFromHost(host) {
  if (!host || typeof host !== 'string') return '';
  try {
    return host.split(':')[0].split('.')[0];
  } catch {
    return '';
  }
}

/**
 * Sends a named event to PostHog with optional properties.
 * Fire-and-forget — errors are caught and logged; failures do not propagate.
 */
export function capture(eventName, properties) {
  try {
    posthog?.capture(eventName, properties);
  } catch (err) {
    console.warn('[PostHog] capture() failed:', err);
  }
}

/** Associates subsequent events with the given user.  Should be called once on login. */
export function identify(userId, properties) {
  try {
    posthog?.identify(userId, properties);
  } catch (err) {
    console.warn('[PostHog] identify() failed:', err);
  }
}

/** Builds the user-property object passed to identify(). */
export function identifyProps(user) {
  if (!user) return {};
  return {
    name: user.name,
    email: user.email,
    primarySignInName: user.username || user.email,
    tenant: getTenantFromHost(user.host)
  };
}

/**  Resets the PostHog session (called on logout). */
export function reset() {
  try {
    posthog?.reset();
  } catch (err) {
    console.warn('[PostHog] reset() failed:', err);
  }
}
